import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import Layout from "@/components/Layout";
import { 
  User, 
  Lock,
  CheckCircle,
  ArrowRight,
  Eye,
  EyeOff
} from "lucide-react";

const Login = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    rememberMe: false
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({
    username: "",
    password: ""
  });

  const [showPassword, setShowPassword] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    // 验证用户名
    if (!formData.username.trim()) {
      newErrors.username = "请输入用户名或邮箱";
    }
    
    // 验证密码
    if (!formData.password) {
      newErrors.password = "请输入密码";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 清除对应字段的错误信息
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // 这里可以添加登录逻辑
      console.log("登录信息:", formData);
      setIsSubmitted(true);
    }
  };

  if (isSubmitted) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-20">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                登录成功！
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                欢迎回来！您已成功登录See系列光学仿真软件账户。
              </p>
              
              <div className="bg-blue-50 rounded-lg p-6 mb-8">
                <h3 className="font-semibold text-blue-900 mb-3">
                  登录信息
                </h3>
                <div className="space-y-2 text-left">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">用户名/邮箱</span>
                    <span className="text-sm font-medium text-gray-900">{formData.username}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">记住我</span>
                    <span className="text-sm font-medium text-gray-900">{formData.rememberMe ? "是" : "否"}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => window.location.href = "/"}>
                  进入首页
                </Button>
                <Button variant="outline" onClick={() => window.location.href = "/products"}>
                  查看产品
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            登录账户
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            登录您的See系列光学仿真软件账户，继续使用我们的产品
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">欢迎回来</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* Username/Email */}
                  <div>
                    <Label htmlFor="username" className="flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span>用户名或邮箱 *</span>
                    </Label>
                    <Input
                      id="username"
                      value={formData.username}
                      onChange={(e) => handleInputChange("username", e.target.value)}
                      placeholder="请输入用户名或邮箱"
                      className={errors.username ? "border-red-500" : ""}
                    />
                    {errors.username && (
                      <p className="text-sm text-red-500 mt-1">{errors.username}</p>
                    )}
                  </div>

                  {/* Password */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="password" className="flex items-center space-x-2">
                        <Lock className="w-4 h-4" />
                        <span>密码 *</span>
                      </Label>
                      <a href="#" className="text-sm text-blue-600 hover:underline">
                        忘记密码？
                      </a>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                        placeholder="请输入密码"
                        className={errors.password ? "border-red-500" : ""}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-sm text-red-500 mt-1">{errors.password}</p>
                    )}
                  </div>

                  {/* Remember Me */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="rememberMe"
                        checked={formData.rememberMe}
                        onCheckedChange={(checked) => handleInputChange("rememberMe", checked || false)}
                      />
                      <Label htmlFor="rememberMe" className="text-sm text-gray-600">
                        记住我
                      </Label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button type="submit" className="w-full">
                    登录
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>

                  {/* Register Link */}
                  <div className="text-center text-sm text-gray-600">
                    没有账户？ <Link to="/register" className="text-blue-600 hover:underline font-medium">去注册</Link>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default Login;